import { buildCollection } from "@firecms/core";

import { cleanUpReplacedUploads } from "../uploadCleanup";
import {
  MAX,
  eyebrow,
  heading,
  paragraphs,
  photo,
  section,
} from "./sections";

// The Contact page: one document (`pages/contact`). Sections are declared in page
// order. The form's fields, submission and status wording stay in the site's
// code, as does the waitlist link; see docs/cms-content-model.md.

/** A plain editable list rendered as bullets or chips. Free to grow. */
const list = (name: string, description: string) => ({
  name,
  dataType: "array" as const,
  description,
  of: {
    dataType: "string" as const,
    validation: { max: MAX.cardTitle },
  },
});

export const contactPageCollection = buildCollection({
  id: "page-contact",
  path: "pages",
  name: "Contact",
  singularName: "Contact page",
  icon: "Email",
  group: "Pages",
  description:
    "Every piece of text on the Contact page, listed in the order it appears on the page.",
  // `pages` holds one document per page; this collection shows only Contact's.
  forceFilter: { pageId: ["==", "contact"] },
  // The page exists already and the site expects it — it can be edited, but
  // not added to or removed.
  permissions: { read: true, edit: true, create: false, delete: false },
  openEntityMode: "full_screen",
  hideIdFromForm: true,
  // Hidden from the drawer; the `pageShortcut` view in App.tsx opens it directly.
  hideFromNavigation: true,
  inlineEditing: false,
  exportable: false,
  // Replacing a photo leaves the old file in the bucket unless we remove it.
  callbacks: cleanUpReplacedUploads,

  properties: {
    hero: section(
      "1. Let's connect",
      "The left-hand side at the top of the page — your invitation, your contact details and the photo below them.",
      {
        eyebrow: eyebrow(),
        heading: heading("The large greeting, e.g. Let's connect."),
        body: paragraphs(
          "Invitation paragraphs",
          "The welcoming lines under the heading, before your contact details.",
        ),
        email: {
          name: "Email address",
          dataType: "string",
          description:
            "Shown beside the envelope symbol. This is only what visitors read — messages from the form are sent to the address set in the site itself.",
          validation: { max: 200 },
        },
        location: {
          name: "Where you work",
          dataType: "string",
          description:
            "Shown beside the map pin, e.g. Sessions available worldwide via Zoom.",
          validation: { max: 200 },
        },
        photo: photo({
          name: "Photo",
          description: "The picture below your contact details.",
          storagePath: "pages/contact",
          shape:
            "It is shown as a wide rectangle, so a landscape photo suits it best.",
        }),
      },
    ),

    form: section(
      "2. The message form",
      "The panel on the right where visitors write to you, what they see after sending, and the waitlist note underneath.",
      {
        heading: heading("The title above the form, e.g. Send a message."),
        namePlaceholder: {
          name: "Faded text in the name box",
          dataType: "string",
          description:
            "The grey hint inside an empty box. It disappears as soon as the visitor starts typing.",
          validation: { max: MAX.ctaLabel },
        },
        emailPlaceholder: {
          name: "Faded text in the email box",
          dataType: "string",
          description: "The grey hint inside the email box.",
          validation: { max: MAX.ctaLabel },
        },
        animalPlaceholder: {
          name: "Faded text in the animal box",
          dataType: "string",
          description: "The grey hint inside the box asking about their animal.",
          validation: { max: MAX.ctaLabel },
        },
        messagePlaceholder: {
          name: "Faded text in the message box",
          dataType: "string",
          description: "The grey hint inside the large message box.",
          validation: { max: MAX.subheading },
        },
        submitLabel: {
          name: "Send button text",
          dataType: "string",
          description:
            "The wording on the button that sends the message. While it is sending it briefly says Sending... , which is set in the site itself.",
          validation: { max: MAX.ctaLabel },
        },
        successHeading: {
          name: "Thank-you title",
          dataType: "string",
          description:
            "The large line a visitor sees once their message has sent, e.g. Message received!",
          validation: { max: MAX.heading },
        },
        successBody: {
          name: "Thank-you message",
          dataType: "string",
          multiline: true,
          description:
            "The smaller line under it. If you promise a reply time here, make sure it is one you can keep.",
          validation: { max: MAX.paragraph },
        },
        successCtaLabel: {
          name: "Thank-you button text",
          dataType: "string",
          description:
            "The button that clears the form so they can write again, e.g. Send another.",
          validation: { max: MAX.ctaLabel },
        },
        waitlistPrompt: {
          name: "Waitlist question",
          dataType: "string",
          description:
            "The line below the form, e.g. Can't find a session time that works for you?",
          validation: { max: MAX.subheading },
        },
        waitlistLinkLabel: {
          name: "Waitlist link text",
          dataType: "string",
          description:
            "The underlined words a visitor clicks. Where it goes is set in the site itself.",
          validation: { max: MAX.subheading },
        },
      },
    ),

    speaking: section(
      "3. Podcasts, Speaking & Collaborations",
      "The green band at the bottom, with the two white cards.",
      {
        eyebrow: eyebrow(),
        heading: heading(),
        body: paragraphs(
          "Introduction paragraphs",
          "The centred lines under the heading, above the two cards.",
        ),
        availableForHeading: {
          name: "Left card title",
          dataType: "string",
          description: "The title on the first card, e.g. I am available for.",
          validation: { max: MAX.cardTitle },
        },
        availableFor: list(
          "Left card list",
          "Each box becomes one bulleted line on the left card. Add, reorder or remove them freely.",
        ),
        topicsHeading: {
          name: "Right card title",
          dataType: "string",
          description:
            "The title on the second card, e.g. Topics I love speaking about.",
          validation: { max: MAX.cardTitle },
        },
        topics: list(
          "Right card topics",
          "Each box becomes one rounded chip on the right card, flowing across the card. Short topics look best. Add, reorder or remove them freely.",
        ),
      },
    ),

    // Identifies which page this document is; set once when seeded.
    pageId: {
      name: "Page id",
      dataType: "string",
      disabled: { hidden: true },
    },
    title: {
      name: "Page title",
      dataType: "string",
      disabled: { hidden: true },
    },
  },

  // The order of the panels in the form — matches the live page top to bottom.
  propertiesOrder: ["hero", "form", "speaking"],
});
